import { Component, OnDestroy, OnInit, HostListener, ChangeDetectionStrategy } from '@angular/core';
import Slideout from 'slideout';
import { Router } from '@angular/router';
import { IUser } from '../../types/datatypes';
import { UserService } from '../../core/services/data/user.service';
import { SettingsService } from '../../core/services/data/settings.service';
import { ModalService } from '../../core/services/modal.service';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})

export class SidebarComponent implements OnInit, OnDestroy {
	public me;

	public author: IUser;

	public cartPurchaseEnabled = false;

	public displayPromoWizard = false;

	public displayResources = false;

	public displayEventRegistration = false;

	public notAuthor = false;

	public navSlideout;

	public author$;

	public hidden = false;

	public smallScreen = false;

	private toggleButtonHandler: () => void;

	get homeLink(): string {
		return `/author-dashboard/${this.author.authorLink}/home`;
	}

	get promotionsLink(): string {
		return `/author-dashboard/${this.author.authorLink}/promotions`;
	}

	get booksLink(): string {
		return `/author-dashboard/${this.author.authorLink}/books`;
	}

	get eventsLink(): string {
		return `/author-dashboard/${this.author.authorLink}/events`;
	}

	get discountsLink(): string {
		return `/author-dashboard/${this.author.authorLink}/discounts`;
	}

	get exclusivesLink(): string {
		return `/author-dashboard/${this.author.authorLink}/exclusives`;
	}

	get resourcesLink(): string {
		return `/author-dashboard/${this.author.authorLink}/resources`;
	}

	get promoWizardLink(): string {
		return `/author-dashboard/${this.author.authorLink}/promo-wizard`;
	}

	get profileLink(): string {
		return `/author-dashboard/${this.author.authorLink}/account/options`;
	}

	get fbGroupLink(): string {
		return 'https://www.facebook.com/groups/538674334318179';
	}

	get showPromoWizardLink(): boolean {
		return this.displayPromoWizard;
	}

	get isMember(): boolean {
		return (this.isPlatinumMember() || this.isGoldMember());
	}

	get aiToolKitSocialWriterLink(): string {
		return `/author-dashboard/${this.author.authorLink}/ai-toolkit/social-writer`;
	}

	get aiToolKitBookDescriptionLink(): string {
		return `/author-dashboard/${this.author.authorLink}/ai-toolkit/book-description`;
	}

	constructor(
		private uss: UserService,
		private ss: SettingsService,
		private ms: ModalService,
		private router: Router,
	) {
	}

	@HostListener('window:resize')
	onResize() {
		this.smallScreen = window.innerWidth < 768;
		if (this.smallScreen) {
			this.hideSidebar();
			this.navSlideout.close();
			return;
		}
		this.showSidebar();
	}

	ngOnInit() {
		this.createSlideout();

		this.smallScreen = window.innerWidth < 768;

		// get view port width
		this.hidden = this.smallScreen;

		const parts = window.location.pathname.split('/');
		this.uss.fetchAuthor(parts[2]);

		this.uss.fetchUser().then(
			// eslint-disable-next-line consistent-return
			(user: IUser) => {
				if (user && !user.password_updated) {
					return this.uss.logout().subscribe(
						() => {
							this.uss.updateUser({});
							window.location.href = '/login';
						},
					);
				}

				this.me = user;
				this.author$ = this.uss.author$.subscribe(
					(data: IUser) => {
						this.author = data;
					},
				);
			},
		).catch(() => {});

		this.ss.fetchCartPurchaseSetting().then(
			(value) => {
				this.cartPurchaseEnabled = !!value;
			},
		);

		this.ss.promoWizardSetting().then(
			(value) => {
				this.displayPromoWizard = value as boolean;
			},
		);

		this.ss.resourcesSetting().then(
			(value) => {
				this.displayResources = value as boolean;
			},
		);

		this.ss.eventRegistrationSetting().then(
			(value) => {
				this.displayEventRegistration = value as boolean;
			},
		);
	}

	ngOnDestroy() {
		this.author$?.unsubscribe();
		document.querySelector('.toggle-button')?.removeEventListener('click', this.toggleButtonHandler);
		this.navSlideout?.destroy();
	}

	createSlideout() {
		this.navSlideout = new Slideout({
			panel: document.getElementById('slider-panel'),
			menu: document.getElementById('slider-menu'),
			// eslint-disable-next-line no-restricted-globals
			padding: screen.width,
		});

		this.navSlideout.on('beforeopen', () => {
			this.showSidebar();
		});
		this.navSlideout.on('close', () => {
			this.hideSidebar();
		});

		this.toggleButtonHandler = () => this.toggleSidebar();
		document.querySelector('.toggle-button').addEventListener('click', this.toggleButtonHandler);
	}

	toggleSidebar() {
		if (!this.smallScreen) return;

		this.navSlideout.toggle();
	}

	showSidebar() {
		if (!this.hidden) return;

		const toggleButton = document.querySelector('.toggle-button');
		if (this.smallScreen) {
			toggleButton.classList.add('nav-open');
			/* this.navSlideout.open(); */
		}
		this.hidden = false;
	}

	hideSidebar() {
		if (this.hidden) return;

		const toggleButton = document.querySelector('.toggle-button');
		toggleButton.classList.remove('nav-open');
		/* this.navSlideout.close(); */
		this.hidden = true;
	}

	isFreeMember() {
		return this.uss.isFreeMember();
	}

	isGoldMember() {
		return this.uss.isGoldMember();
	}

	isPlatinumMember() {
		return this.uss.isPlatinumMember();
	}

	validateLink(link) {
		if (!this.isMember) {
			this.ms.open('premiumContentModal');
			return;
		}

		this.router.navigate([link]).then(() => this.toggleSidebar());
	}

	upgrade(event) {
		if (event) {
			this.ms.open('update-card-form');
		} else {
			this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
				this.router.navigate([`/author-dashboard/${this.author.authorLink}/subscriptions`]);
			});
		}
	}

	goToDashboard(event) {
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			this.router.navigate([this.homeLink]);
		});
	}

	logout() {
		this.uss.logout().subscribe(
			() => {
				this.uss.updateUser({});
				window.location.href = '/login';
			},
		);
	}
}
